import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { PRODUCTS, SLOGANS, CARD_VARIANTS } from '../data/products';
import { useReveal } from '../hooks/useReveal';
import './Home.css';

/* ═══════════════════════════════════════════
   HERO SECTION
═══════════════════════════════════════════ */
function Hero() {
  const [sloganIdx, setSloganIdx] = useState(0);
  const [letters, setLetters] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    setLetters(SLOGANS[sloganIdx].split(''));
    const t = setInterval(() => {
      setSloganIdx(i => (i + 1) % SLOGANS.length);
    }, 4200);
    return () => clearInterval(t);
  }, [sloganIdx]);

  // Particles
  useEffect(() => {
    const container = document.getElementById('particles');
    if (!container) return;
    container.innerHTML = '';
    const colors = [
      'rgba(245,166,35,0.45)', 'rgba(14,165,233,0.3)',
      'rgba(34,197,94,0.25)', 'rgba(245,166,35,0.2)'
    ];
    for (let i = 0; i < 20; i++) {
      const d = document.createElement('div');
      const size = Math.random() * 4 + 2;
      d.className = 'particle';
      d.style.cssText = `
        width:${size}px;height:${size}px;
        left:${Math.random()*100}%;
        border-radius:50%;position:absolute;
        background:${colors[i % colors.length]};
        animation:floatParticle ${Math.random()*12+8}s linear ${Math.random()*8}s infinite;
      `;
      container.appendChild(d);
    }
  }, []);

  const scrollToProducts = () => {
    navigate('/products');
  };

  return (
    <section className="hero">
      <img src="/banner.jpg" alt="banner" className="hero-bg" />
      <div className="hero-overlay" />
      <div id="particles" className="particles-container" />

      <div className="hero-content container">
        <div className="hero-left fade-up">
          <div className="hero-badge">
            <span className="badge-dot" />
            Powered by Artificial Intelligence
          </div>

          <h1 className="hero-h1">
            <span className="h1-normal">Intelligent Software</span>
            <br />
            <span className="h1-gold">Built for Tomorrow</span>
          </h1>

          {/* Animated slogan */}
          <div className="slogan-box" key={sloganIdx}>
            {letters.map((ch, i) => (
              <span
                key={i}
                className={`slogan-letter ${i % 2 === 0 ? 'from-left' : 'from-right'}`}
                style={{ animationDelay: `${i * 0.05}s` }}
              >
                {ch === ' ' ? '\u00A0' : ch}
              </span>
            ))}
          </div>

          <div className="hero-btns">
            <button className="btn-gold" onClick={scrollToProducts}>
              🚀 Explore Products
            </button>
            <button className="btn-outline" onClick={() => navigate('/contact')}>
              Contact Us →
            </button>
          </div>
        </div>

        {/* Right side — contact strips */}
        <div className="hero-right fade-up" style={{ animationDelay: '0.2s' }}>
          <a href="tel:+918121325297" className="contact-strip">
            <img src="/phone.png" alt="Phone" className="strip-img" />
            <div>
              <div className="strip-label">Call Us</div>
              <div className="strip-value">+91 8121325297</div>
            </div>
          </a>
          <a href="mailto:info@visiontechnoservices.com" className="contact-strip">
            <img src="/email.png" alt="Email" className="strip-img" />
            <div>
              <div className="strip-label">Email Us</div>
              <div className="strip-value">info@visiontechnoservices.com</div>
            </div>
          </a>
          <button className="contact-strip" onClick={() => navigate('/contact')}>
            <img src="/address.png" alt="Address" className="strip-img" />
            <div>
              <div className="strip-label">Our Office</div>
              <div className="strip-value">Mallapur, Hyderabad, Telangana</div>
            </div>
          </button>
        </div>
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════
   DASHBOARD / STATS
═══════════════════════════════════════════ */
function StatCard({ target, suffix, title, sub, delay }) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const animated = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !animated.current) {
        animated.current = true;
        let start = 0;
        const step = Math.ceil(target / 40);
        const t = setInterval(() => {
          start = Math.min(start + step, target);
          setCount(start);
          if (start >= target) clearInterval(t);
        }, 35);
      }
    }, { threshold: 0.5 });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [target]);

  return (
    <div className="dash-card" ref={ref} style={{ animationDelay: delay }}>
      <div className="dash-num">
        <span>{count}</span>{suffix}
      </div>
      <div className="dash-title">{title}</div>
      <div className="dash-sub">{sub}</div>
    </div>
  );
}

function Dashboard() {
  return (
    <section className="dashboard">
      <div className="container">
        <div className="dash-label">Platform at a Glance</div>
        <div className="dash-grid">
          <StatCard target={20} suffix="+" title="AI Products" sub="Across 10+ industries" delay="0s" />
          <StatCard target={24} suffix="/7" title="AI Availability" sub="Always-on intelligent systems" delay="0.1s" />
          <StatCard target={100} suffix="%" title="Custom Solutions" sub="Tailored to your needs" delay="0.2s" />
          <StatCard target={10} suffix="+" title="Industries Served" sub="Education, Health, Finance & more" delay="0.3s" />
        </div>
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════
   PRODUCT CARD
═══════════════════════════════════════════ */
function ProductCard({ product, index, variant }) {
  const navigate = useNavigate();
  return (
    <div
      className={`prod-card ${variant} reveal`}
      style={{ animationDelay: `${(index % 4) * 0.07}s` }}
      onClick={() => navigate(`/product/${product.id}`)}
    >
      <div className="card-bar" />
      <span className="card-idx">{String(index + 1).padStart(2, '0')}</span>
      <span className="card-emoji">{product.emoji}</span>
      <h3 className="card-name">{product.name}</h3>
      <ul className="card-feats">
        {product.points.map((pt, i) => (
          <li key={i} className="card-feat">
            <span className="feat-bullet" />
            {pt}
          </li>
        ))}
      </ul>
      <button className="card-btn">View Details →</button>
    </div>
  );
}

/* ═══════════════════════════════════════════
   PRODUCTS SECTION
═══════════════════════════════════════════ */
function ProductsSection() {
  const gridRef = useReveal();

  return (
    <section id="products" className="products-section">
      <div className="sec-head">
        <div className="reveal sec-eyebrow">Our AI Product Suite</div>
        <h2 className="reveal sec-title">
          Transforming Industries with <em>Intelligence</em>
        </h2>
        <p className="reveal sec-sub">
          Explore our AI-powered solutions built for education, healthcare, finance, real estate, and more.
        </p>
      </div>

      <div className="prod-grid container" ref={gridRef}>
        {PRODUCTS.map((product, i) => (
          <ProductCard
            key={product.id}
            product={product}
            index={i}
            variant={CARD_VARIANTS[i % CARD_VARIANTS.length]}
          />
        ))}
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════
   HOME PAGE
═══════════════════════════════════════════ */
export default function Home() {
  // Check hash on mount
  useEffect(() => {
    if (window.location.hash === '#products') {
      setTimeout(() => {
        document.getElementById('products')?.scrollIntoView({ behavior: 'smooth' });
      }, 300);
    }
  }, []);

  return (
    <>
      <Hero />
      <Dashboard />
      <ProductsSection />
    </>
  );
}
